/* Custom size + swatch ordering on catalog rug pages (Maia Homes / Eurus 9.11.1).
 *
 * HYBRID design (PM-reviewed): the theme's own Size DROPDOWN keeps the real
 * sizes; the two special purchase modes are always-visible buttons rendered in
 * the widget's OWN markup beneath the picker (snippets/rug-size-options.liquid).
 * Nothing is injected into the theme's re-rendering picker DOM — which removes,
 * structurally, the re-injection defect class the sister store shipped (chips
 * rebuilt without state, MutationObserver races, card-picker collisions).
 *
 * The estimate shown here mirrors the backend ladder exactly: price is
 * interpolated between the two LISTED sizes bracketing the area, so it always
 * lands between its neighbours, never shrinks as the rug grows, and an exact
 * listed size costs exactly its listed price. The charged price is ALWAYS
 * recomputed server-side — this script only displays; the request carries
 * dimensions, never a price.
 *
 * THE STATE RULE: the two mode buttons (aria-pressed) are the single source of
 * truth for what Add-to-cart will add. They live outside the picker, so the
 * theme cannot destroy them mid-selection; and ANY change inside the theme's
 * variant picker — size, colour, anything — clears the special mode, so the
 * shopper can never see a normal size at a normal price while the cart would
 * receive a swatch (the sister store's worst shipped defect).
 */
(function () {
  'use strict';

  if (window.__mhRugSizeOptionsLoaded) return;
  window.__mhRugSizeOptionsLoaded = true;

  /* ---------------- price maths (mirrors apps/ruggen/utils/pricing.py) ------ */

  // One side of a size, in every form this catalog uses:
  //   8'   8 ft        6'6"  6.5 ft (mixed)
  //   12"  1 ft        14    bare number = feet
  // Parsed per-dimension, not whole-title: dropping mixed titles like
  // "4' x 6'6"" silently removes real ladder anchors, which makes a custom size
  // at exactly that listed size quote LESS than the listed variant.
  var DIM_RE = /^\s*(\d+(?:\.\d+)?)\s*(?:(["”″])|['′’]\s*(?:(\d+(?:\.\d+)?)\s*["”″]?)?)?\s*$/;

  var MIN_SHORT_FT = 2.0;   // mirrors pricing.MIN_CUSTOM_SHORT_FT
  var MIN_LONG_FT = 3.0;    // mirrors pricing.MIN_CUSTOM_LONG_FT
  var SWATCH_ANCHOR_SQFT = 1.0;

  function dimToFeet(token) {
    var m = DIM_RE.exec(token);
    if (!m) return null;
    var n = parseFloat(m[1]);
    if (isNaN(n)) return null;
    if (m[2]) return n / 12;                       // pure inches
    return n + (m[3] ? parseFloat(m[3]) / 12 : 0); // feet, optional inches
  }

  function ladderDims(title) {
    // Multi-option variants title as "3' x 5' / Magenta" — everything after
    // the option separator is not a dimension. Size is always the first option
    // on this catalog.
    var sizePart = String(title).split('/')[0];
    var parts = sizePart.replace(/^\s*swatch\s+/i, '').split(/[x×]/i);
    if (parts.length !== 2) return null;
    var w = dimToFeet(parts[0]);
    var l = dimToFeet(parts[1]);
    if (w === null || l === null || !(w > 0) || !(l > 0)) return null;
    return [w, l];
  }

  function roundToX99(price) {
    // Snap to 6dp first: an exact total like 8976.0 can arrive from binary
    // floating point as 8975.999999999998, which would otherwise floor a dollar
    // low — a visible price jump driven purely by float noise.
    price = Math.round(price * 1e6) / 1e6;
    var floored = Math.floor(price);
    var candidate = floored + 0.99;
    if (candidate < price) candidate = floored + 1.99;
    return Math.round(candidate * 100) / 100;
  }

  /* The listed variant whose DIMENSIONS match exactly, if any. Area alone cannot
   * price every rug: 3'x5' and 2.5'x6' are both 15 sqft but listed at different
   * prices, and averaging them quoted LESS than the listed 3'x5'. An exact
   * dimension match always wins, orientation-insensitively. */
  function exactListedPrice(variants, widthFt, lengthFt) {
    var want = [Math.round(widthFt * 100) / 100, Math.round(lengthFt * 100) / 100]
      .sort(function (a, b) { return a - b; });
    for (var i = 0; i < (variants || []).length; i++) {
      var v = variants[i];
      var d = ladderDims(String(v.title || ''));
      if (!d) continue;
      var have = [Math.round(d[0] * 100) / 100, Math.round(d[1] * 100) / 100]
        .sort(function (a, b) { return a - b; });
      if (have[0] !== want[0] || have[1] !== want[1]) continue;
      var price = (v.price || 0) / 100;   // Liquid variant JSON is CENTS
      if (!(price > 0)) continue;
      var compare = (v.compare_at_price || 0) / 100;
      return { price: roundToX99(price), compareAt: compare > price ? roundToX99(compare) : 0 };
    }
    return null;
  }

  function ladderFromVariants(variants, swatchAnchor) {
    var byArea = {};
    (variants || []).forEach(function (v) {
      var d = ladderDims(String(v.title || ''));
      if (!d) return;
      var sqft = d[0] * d[1];
      var price = (v.price || 0) / 100;
      if (!(sqft > 0) || !(price > 0)) return;
      var compare = (v.compare_at_price || 0) / 100;
      (byArea[sqft] = byArea[sqft] || []).push({ price: price, compare: compare > price ? compare : 0 });
    });
    // Re-supply the 1 sqft anchor the deleted swatch variant used to provide.
    // Conditional, so it is a NO-OP while a real swatch variant still exists —
    // which is what lets the tag and the variant coexist during migration.
    if (swatchAnchor > 0 && !byArea[SWATCH_ANCHOR_SQFT]) {
      byArea[SWATCH_ANCHOR_SQFT] = [{ price: swatchAnchor, compare: 0 }];
    }
    return Object.keys(byArea).map(function (key) {
      var rows = byArea[key];
      var compares = rows.filter(function (r) { return r.compare; });
      return {
        sqft: parseFloat(key),
        // Duplicate areas are averaged so one area maps to one price
        // (3'x8' and 4'x6' are both 24 sqft).
        price: rows.reduce(function (s, r) { return s + r.price; }, 0) / rows.length,
        compare: compares.length
          ? compares.reduce(function (s, r) { return s + r.compare; }, 0) / compares.length
          : 0
      };
    }).sort(function (a, b) { return a.sqft - b.sqft; });
  }

  function bracketPrice(anchors, sqft) {
    var first = anchors[0];
    var last = anchors[anchors.length - 1];
    if (sqft <= first.sqft) return (first.value / first.sqft) * sqft;
    if (sqft >= last.sqft) return (last.value / last.sqft) * sqft;
    for (var i = 0; i < anchors.length - 1; i++) {
      var lo = anchors[i], hi = anchors[i + 1];
      if (sqft >= lo.sqft && sqft <= hi.sqft) {
        return lo.value + (hi.value - lo.value) * (sqft - lo.sqft) / (hi.sqft - lo.sqft);
      }
    }
    return (last.value / last.sqft) * sqft;
  }

  function priceFor(ladder, sqft) {
    var raw = bracketPrice(ladder.map(function (p) { return { sqft: p.sqft, value: p.price }; }), sqft);
    var price = roundToX99(raw);
    var compareAnchors = ladder.filter(function (p) { return p.compare; })
      .map(function (p) { return { sqft: p.sqft, value: p.compare }; });
    var compareAt = 0;
    if (compareAnchors.length >= 2) {
      compareAt = roundToX99(bracketPrice(compareAnchors, sqft));
      if (compareAt <= price) compareAt = 0;
    }
    return { price: price, compareAt: compareAt };
  }

  /* ---------------- money ---------------------------------------------------
   * Uses the SHOP's money_format pattern rather than a hardcoded en-US USD
   * number, so a non-USD market renders its own currency and separators. */
  function moneyFormatter(format) {
    var pattern = format || '${{amount}}';
    return function (amount) {
      return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
        var decimals = token.indexOf('no_decimals') > -1 ? 0 : 2;
        var comma = token.indexOf('comma_separator') > -1;
        var fixed = amount.toFixed(decimals);
        var bits = fixed.split('.');
        bits[0] = bits[0].replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
        return bits.length > 1 ? bits.join(comma ? ',' : '.') : bits[0];
      });
    };
  }

  /* ---------------- theme cart contract ------------------------------------ */

  function themeCartAvailable() {
    return !!(window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper') &&
      window.Eurus && window.Eurus.cart_add_url && typeof getSectionInnerHTML === 'function');
  }

  /* Eurus's own cart-add contract, so the shopper stays on the page and the
   * drawer opens over it. Deliberately NOT the /cart/<variant>:<qty> permalink
   * the API also returns: that leaves the storefront for *.myshopify.com AND
   * REPLACES the cart, discarding whatever the shopper already had. */
  function appendProperties(formData, properties) {
    Object.keys(properties || {}).forEach(function (key) {
      formData.append('properties[' + key + ']', properties[key]);
    });
  }

  function addVariantToThemeCart(variantId, qty, properties) {
    var sections = window.Alpine.store('xCartHelper').getSectionsToRender();
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(qty || 1));
    appendProperties(formData, properties);
    formData.append('sections', sections.map(function (s) { return s.id; }));
    formData.append('sections_url', window.location.pathname);

    return fetch(window.Eurus.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (response) {
      if (String(response.status) === '422') {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }
      sections.forEach(function (section) {
        var el = document.querySelector(section.selector);
        if (el && response.sections && response.sections[section.id]) {
          el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
        }
      });
      if (window.Alpine.store('xMiniCart')) window.Alpine.store('xMiniCart').openCart();
      var bubble = document.querySelector('#cart-icon-bubble span');
      if (bubble) window.Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10);
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      return response;
    });
  }

  function addThenGoToCart(variantId, qty, properties) {
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(qty || 1));
    appendProperties(formData, properties);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (body) {
      if (body && body.status && String(body.status) !== '200') {
        throw new Error(body.description || body.message || 'Add to cart failed');
      }
      window.location.href = '/cart';
    });
  }

  function addToCart(variantId, qty, properties) {
    if (themeCartAvailable()) {
      return addVariantToThemeCart(variantId, qty, properties).catch(function () {
        return addThenGoToCart(variantId, qty, properties);
      });
    }
    return addThenGoToCart(variantId, qty, properties);
  }

  /* ---------------- widget -------------------------------------------------- */

  function readJson(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function initWidget(root) {
    if (root.hasAttribute('data-rso-ready')) return;
    root.setAttribute('data-rso-ready', '');

    var config = readJson(root, '[data-rso-config]') || {};
    var rawVariants = readJson(root, '[data-rso-ladder]') || [];
    var money = moneyFormatter(config.moneyFormat);

    // Scope to this widget's own product section: the same section markup is
    // rendered by quick view, and `.variant-selects-main` is emitted in exactly
    // one place per section (product-template.liquid).
    var scope = root.closest('.shopify-section') || document;

    var ladder = ladderFromVariants(rawVariants, config.swatchAnchor || 0);
    // Fewer than two points gives no curve to price from; extrapolating a whole
    // product from one anchor invites far-off errors. Matches the backend's
    // "needs at least two listed sizes" rule.
    var customSizeUsable = !!config.customSize && ladder.length >= 2;
    var swatchUsable = !!config.swatch;

    var el = {
      customBtn: root.querySelector('[data-rso-mode-btn="custom"]'),
      swatchBtn: root.querySelector('[data-rso-mode-btn="swatch"]'),
      panel: root.querySelector('[data-rso-panel]'),
      shape: root.querySelector('[data-rso-shape]'),
      shapeRow: root.querySelector('[data-rso-shape-row]'),
      widthLabel: root.querySelector('[data-rso-width-label]'),
      widthFt: root.querySelector('[data-rso-width-ft]'),
      widthIn: root.querySelector('[data-rso-width-in]'),
      lengthRow: root.querySelector('[data-rso-length-row]'),
      lengthFt: root.querySelector('[data-rso-length-ft]'),
      lengthIn: root.querySelector('[data-rso-length-in]'),
      price: root.querySelector('[data-rso-price]'),
      compare: root.querySelector('[data-rso-compare]'),
      error: root.querySelector('[data-rso-error]')
    };
    if (customSizeUsable && (!el.panel || !el.widthFt)) customSizeUsable = false;
    // The ladder gate can fail where the Liquid gate passed (unparseable size
    // titles): hide the button the shopper could otherwise click into a dead
    // panel, and leave the static custom-quote link as the fallback path.
    if (!customSizeUsable && el.customBtn) el.customBtn.hidden = true;
    if (!swatchUsable && el.swatchBtn) el.swatchBtn.hidden = true;
    if (!customSizeUsable && !swatchUsable) return;

    root.hidden = false;
    if (el.shapeRow && el.shape && el.shape.options.length < 2) el.shapeRow.hidden = true;

    // Flips on the first manual panel edit; until then, opening the panel
    // re-seeds its dims from the size dropdown's current pick.
    var dimsTouched = false;

    // Default the panel to the size the shopper is ALREADY looking at, so the
    // flow reads as "tweak the size I chose" and the opening quote matches the
    // price on screen. (A mid-ladder-by-sqft default can land on a degenerate
    // runner — 3'x20' on this catalog — and the panel then opens quoting
    // triple the PDP price.) Mid-ladder stays as the fallback for size titles
    // the parser can't read.
    function presetDefaults() {
      if (!el.widthFt) return;
      var dims = null;
      var host = picker();
      if (host) {
        Array.prototype.forEach.call(host.querySelectorAll('select'), function (sel) {
          if (!dims && isSizeSelect(sel)) dims = ladderDims(String(sel.value || ''));
        });
      }
      if (!dims) {
        var mid = ladder.length ? ladder[Math.floor(ladder.length / 2)] : null;
        for (var i = 0; i < rawVariants.length && !dims; i++) {
          var d = ladderDims(String(rawVariants[i].title || ''));
          if (d && mid && Math.abs(d[0] * d[1] - mid.sqft) < 0.01) dims = d;
        }
      }
      if (!dims) return;
      setSelect(el.widthFt, Math.floor(dims[0]));
      setSelect(el.lengthFt, Math.floor(dims[1]));
    }

    function setSelect(select, value) {
      if (!select) return;
      var found = Array.prototype.some.call(select.options, function (o) {
        return parseInt(o.value, 10) === value;
      });
      if (found) select.value = String(value);
    }

    function shape() { return el.shape ? el.shape.value : 'rectangular'; }
    // Round and square have ONE dimension. A width x length form on the
    // all-square-ladder round jute rugs would happily quote a "round" rug at
    // 5' x 9'.
    function singleDimension() { return shape() === 'round' || shape() === 'square'; }

    function dims() {
      var width = parseInt(el.widthFt.value, 10) + parseInt(el.widthIn.value, 10) / 12;
      var length = singleDimension()
        ? width
        : parseInt(el.lengthFt.value, 10) + parseInt(el.lengthIn.value, 10) / 12;
      return { width: width, length: length };
    }

    function sqftOf(d) {
      return shape() === 'round' ? Math.PI * Math.pow(d.width / 2, 2) : d.width * d.length;
    }

    // Mirrors the serializer's non-field validate(). Sorting is the point:
    // 3'x2' is the same rug as 2'x3', so the rule bounds the SHORT and LONG
    // side. A round/square rug has one dimension, which therefore has to clear
    // the LONGER floor — the smallest round rug is 3 ft across, not 2 ft.
    function belowMinimum(d) {
      var sides = [d.width, d.length].sort(function (a, b) { return a - b; });
      if (sides[0] < MIN_SHORT_FT || sides[1] < MIN_LONG_FT) {
        return shape() === 'round' ? config.minRoundText : config.minSizeText;
      }
      return null;
    }

    function quote() {
      var d = dims();
      var problem = belowMinimum(d);
      if (problem) return { error: problem };
      // An exact dimension match wins over the area ladder. Round is excluded:
      // a circle's area never corresponds to a listed W x L variant.
      var exact = shape() === 'round' ? null : exactListedPrice(rawVariants, d.width, d.length);
      var q = exact || priceFor(ladder, sqftOf(d));
      return { price: q.price, compareAt: q.compareAt, dims: d };
    }

    function renderPanel() {
      if (!customSizeUsable) return;
      if (el.lengthRow) el.lengthRow.hidden = singleDimension();
      if (el.widthLabel) {
        el.widthLabel.textContent = shape() === 'round' ? config.labelDiameter
          : shape() === 'square' ? config.labelSide : config.labelWidth;
      }
      var q = quote();
      if (q.error) {
        el.price.textContent = '';
        if (el.compare) el.compare.hidden = true;
        showError(q.error);
      } else {
        el.price.textContent = money(q.price);
        if (el.compare) {
          el.compare.textContent = q.compareAt ? money(q.compareAt) : '';
          el.compare.hidden = !q.compareAt;
        }
        showError(null);
      }
      if (currentSelection() === 'custom') syncStickyChip('custom');
      syncAddButton();
    }

    function showError(message) {
      if (!el.error) return;
      el.error.textContent = message || '';
      el.error.hidden = !message;
    }

    /* ------------ selection state ------------
     * The mode buttons in OUR markup are the single source of truth
     * (aria-pressed). The theme never re-renders them, so the state cannot be
     * stranded by a picker rebuild; and any change inside the picker clears it
     * (see the change listener below). */
    function picker() { return scope.querySelector('.variant-selects-main'); }

    function currentSelection() {
      var active = root.querySelector('[data-rso-mode-btn][aria-pressed="true"]');
      return active ? active.getAttribute('data-rso-mode-btn') : null;
    }

    function select(kind) {
      [el.customBtn, el.swatchBtn].forEach(function (btn) {
        if (btn) btn.setAttribute('aria-pressed', String(btn.getAttribute('data-rso-mode-btn') === kind));
      });
      if (el.customBtn) el.customBtn.setAttribute('aria-expanded', String(kind === 'custom'));
      if (el.panel) el.panel.hidden = kind !== 'custom';
      // Visible mutual exclusivity (the PM's one named risk for this design):
      // while a special mode is active, every theme-owned variant select dims —
      // main picker AND sticky bar — so no dropdown reads as what the Add
      // button will add.
      var host = picker();
      if (host) host.classList.toggle('rso-muted', !!kind);
      Array.prototype.forEach.call(scope.querySelectorAll('select'), function (sel) {
        if (!root.contains(sel)) sel.classList.toggle('rso-muted-el', !!kind);
      });
      syncStickyChip(kind);
      if (kind === 'custom') track('custom_panel_open', { product_id: config.productId });
      if (kind === 'swatch') track('swatch_selected', { product_id: config.productId });
      // Dynamic checkout (Buy It Now) goes straight to a wallet sheet and
      // structurally cannot carry a server-minted custom variant — with a mode
      // active it would silently buy the dropdown's standard size. Hidden, not
      // intercepted: a missing express button costs a click; an express button
      // that checks out the wrong rug costs a return and a remake.
      Array.prototype.forEach.call(
        scope.querySelectorAll('[id^="x-payment-button-"]'),
        function (node) { node.classList.toggle('rso-express-hidden', !!kind); }
      );
      // Until the shopper has touched the panel, opening it re-seeds the dims
      // from the size currently picked in the dropdown — "tweak the size I
      // chose". Once touched, their work-in-progress dims win.
      if (kind === 'custom' && !dimsTouched) presetDefaults();
      if (kind === 'custom') renderPanel();
      syncAddButton();
      if (kind === 'custom' && el.panel) {
        el.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    /* The sticky bar mirrors the mode, it does not own it: while a special
     * mode is active its size select is swapped for a plain text chip of the
     * current selection, so the bar can never display one thing and add
     * another. Sticky selects are the ones outside both our root and the main
     * picker. */
    function syncStickyChip(kind) {
      var mainPicker = picker();
      Array.prototype.forEach.call(scope.querySelectorAll('select'), function (sel) {
        if (root.contains(sel)) return;
        if (mainPicker && mainPicker.contains(sel)) return;
        var wrapper = sel.closest('.select') || sel;
        var chip = wrapper.parentElement && wrapper.parentElement.querySelector('[data-rso-sticky-chip]');
        if (!kind) {
          wrapper.classList.remove('rso-hidden');
          if (chip) chip.remove();
          return;
        }
        var text;
        if (kind === 'swatch') {
          text = (config.atcSwatchLabel || 'Sample').replace(/^Add | to cart$/g, '');
          text = text.charAt(0).toUpperCase() + text.slice(1) +
            ' — ' + (config.swatchPriceMoney || money(config.swatchPrice || 0));
        } else {
          var d = dims();
          text = 'Custom ' + Math.floor(d.width) + "'" +
            (singleDimension() ? '' : " × " + Math.floor(d.length) + "'");
        }
        wrapper.classList.add('rso-hidden');
        if (!chip) {
          chip = document.createElement('span');
          chip.setAttribute('data-rso-sticky-chip', '');
          chip.className = 'rso__sticky-chip';
          wrapper.insertAdjacentElement('afterend', chip);
        }
        // Write-only-on-change: reassertMode() re-runs this from the
        // MutationObserver, and an identical textContent write is still a
        // childList mutation (see armSentinels for the loop that shipped).
        if (chip.textContent !== text) chip.textContent = text;
      });
    }

    function deselect() { select(null); }

    /* Re-assert an active mode's visuals over markup the theme just replaced —
     * a colour change mid-mode re-renders the price, sticky bar and express
     * buttons, and the fresh nodes arrive without our classes or chip. Runs
     * from the MutationObserver, so every write in here (and in the sync
     * functions it calls) is write-only-on-change; class re-adds are free
     * because the observer watches childList only. */
    function reassertMode() {
      var kind = currentSelection();
      if (!kind) return;
      var host = picker();
      if (host) host.classList.add('rso-muted');
      Array.prototype.forEach.call(scope.querySelectorAll('select'), function (sel) {
        if (!root.contains(sel)) sel.classList.add('rso-muted-el');
      });
      Array.prototype.forEach.call(
        scope.querySelectorAll('[id^="x-payment-button-"]'),
        function (node) { node.classList.add('rso-express-hidden'); }
      );
      syncStickyChip(kind);
      syncAddButton();
    }

    function bindModeButton(btn, kind) {
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        select(currentSelection() === kind ? null : kind);
      });
    }

    /* ------------ price display ------------
     * The theme's Add to cart charges whatever mode is selected, so the PDP
     * price block must show that amount. Original markup is stashed ON EACH
     * NODE (not index-matched — a changing node count would land saved markup
     * in the wrong node). */
    function priceNodes() {
      return Array.prototype.slice.call(scope.querySelectorAll('.main-product-price'));
    }

    function syncPrice() {
      var kind = currentSelection();
      var want = null;
      if (kind === 'swatch') {
        want = config.swatchPriceMoney || money(config.swatchPrice || 0);
      } else if (kind === 'custom') {
        var q = quote();
        want = q.error ? '—' : money(q.price);
      }
      priceNodes().forEach(function (node) {
        if (!kind) {
          // Idle. Restore ONLY nodes that currently carry OUR text (marked when
          // we wrote them) — blanket-restoring here re-applied a stale stash
          // over the theme's own fresh price (caught in browser testing: the
          // display snapped back to the old price after every variant change).
          // Then re-stash from whatever the THEME is now showing, so the next
          // activation has a trusted original.
          if (node.hasAttribute('data-rso-price-ours')) {
            node.innerHTML = node.getAttribute('data-rso-price-original');
            node.removeAttribute('data-rso-price-ours');
          }
          node.removeAttribute('data-rso-price-written');
          node.setAttribute('data-rso-price-original', node.innerHTML);
          return;
        }
        // Active. Content that is not byte-for-byte our own last write is
        // theme-authored — a colour change mid-mode re-renders the node with
        // the new variant's price — and THAT fresh markup is the trusted
        // original to stash and later restore. (Before the mode survived
        // colour changes, stashing only happened at idle; keeping that rule
        // here would restore a price from before the colour change.)
        if (node.textContent !== node.getAttribute('data-rso-price-written')) {
          node.setAttribute('data-rso-price-original', node.innerHTML);
        }
        node.setAttribute('data-rso-price-ours', '1');
        // Write-only-on-change: reassertMode() calls this from the
        // MutationObserver, and an identical write is still a mutation.
        if (node.textContent !== want) node.textContent = want;
        node.setAttribute('data-rso-price-written', want);
      });
    }

    /* ------------ the one Add to cart button ------------
     * Exactly ONE add button on the page; it adds whatever is selected, and its
     * label is the disambiguation. With no special mode active it keeps the
     * THEME's own label (which may be translated or context-dependent —
     * "Pre-order", "Sold out"). */
    function addButtons() {
      // name="add" is the main form button; .add_to_cart_button catches the
      // sticky bar's button, which carries only the class. Deduped — the main
      // button has both.
      var found = scope.querySelectorAll('button[name="add"], button.add_to_cart_button');
      return Array.prototype.slice.call(found).filter(function (btn, i, all) {
        return all.indexOf(btn) === i && !root.contains(btn);
      });
    }

    function syncAddButton() {
      syncPrice();
      var kind = currentSelection();
      addButtons().forEach(function (btn) {
        var span = btn.querySelector('.x-atc-text');
        if (!btn.hasAttribute('data-rso-original')) {
          btn.setAttribute('data-rso-original', span ? span.textContent : (btn.textContent || ''));
        }
        var label = btn.getAttribute('data-rso-original');
        var blocked = null;

        if (kind === 'swatch') {
          label = config.atcSwatchLabel || 'Add sample to cart';
        } else if (kind === 'custom') {
          var q = quote();
          // Disabled WITH A REASON, never hidden — a vanishing button reads as
          // a broken page. The reason IS the label, so it cannot be missed.
          blocked = q.error || null;
          label = blocked || (config.atcCustomLabel || 'Add custom size to cart');
        }
        if (span) {
          if (span.textContent !== label) span.textContent = label;
        } else if (btn.textContent !== label) {
          btn.textContent = label;
        }
        if (blocked) btn.setAttribute('data-rso-blocked', '1');
        else btn.removeAttribute('data-rso-blocked');
        btn.classList.toggle('rso-blocked', !!blocked);
      });
    }

    function setBusy(btn, on) {
      btn.disabled = on;
      // Drive the theme's own Alpine `loading` flag so the busy state looks
      // exactly like a normal add. The label rewrite is only the
      // not-yet-booted fallback.
      var data = window.Alpine && window.Alpine.$data ? window.Alpine.$data(btn) : null;
      if (data && typeof data.loading !== 'undefined') { data.loading = on; return; }
      var span = btn.querySelector('.x-atc-text');
      if (span) span.style.opacity = on ? '0.5' : '';
    }

    function endpoint(path) {
      if (config.proxyPath) return config.proxyPath.replace(/\/$/, '') + '/' + path;
      return (config.apiBaseUrl || '').replace(/\/$/, '') + '/api/ruggen/' + path;
    }

    function track(name, data) {
      if (window.dataLayer && window.dataLayer.push) {
        window.dataLayer.push(Object.assign({ event: 'rug_' + name }, data || {}));
      }
    }

    function request(path, body) {
      return fetch(endpoint(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (payload) {
          if (!r.ok) {
            // Minimum-size failures come back from the serializer's non-field
            // validate() as {"non_field_errors": [...]}, not the {"error": ...}
            // shape everything else uses. Handle both.
            throw new Error(payload.error || Object.values(payload || {})[0] || config.errorText);
          }
          return payload;
        });
      }).then(function (payload) {
        // MOCK_VARIANT means the backend has no store credentials — never send a
        // live shopper to that dead cart link.
        if (payload.checkout_url && payload.checkout_url.indexOf('MOCK_VARIANT') > -1) {
          throw new Error(config.errorText);
        }
        if (!payload.variant_id) throw new Error(config.errorText);
        return payload;
      });
    }

    /* The backend mints the one-off from product id + dimensions only — it
     * knows nothing about the rug's OTHER options. On multi-option rugs
     * (Size + Color) the chosen colourway must still reach the order, so every
     * non-size selection rides along as a line-item property, visible in the
     * cart, at checkout and on the order for fulfilment. Values come from the
     * controls' value attributes, which never carry the price-label suffix. */
    function nonSizeSelections() {
      var host = picker();
      if (!host) return {};
      var out = {};
      Array.prototype.forEach.call(host.querySelectorAll('select'), function (sel) {
        var name = (sel.getAttribute('name') || '').replace(/-\d+\s*$/, '').trim();
        if (!name || /^size$/i.test(name)) return;
        if (sel.value && sel.value.indexOf('__rso_') !== 0) out[name] = sel.value;
      });
      Array.prototype.forEach.call(host.querySelectorAll('input[type="radio"]:checked'), function (radio) {
        var name = (radio.getAttribute('name') || '').replace(/-\d+\s*$/, '').trim();
        if (!name || /^size$/i.test(name)) return;
        if (radio.value) out[name] = radio.value;
      });
      return out;
    }

    function quantity() {
      var input = scope.querySelector('input[name="quantity"]');
      var n = input ? parseInt(input.value, 10) : 1;
      return n > 0 ? n : 1;
    }

    function order(btn) {
      var kind = currentSelection();
      if (!kind) return;
      if (btn.hasAttribute('data-rso-blocked')) {
        showError(quote().error);
        return;
      }
      var qty = quantity();
      setBusy(btn, true);
      showError(null);

      var pending;
      if (kind === 'swatch') {
        track('swatch_add_attempt', { product_id: config.productId });
        pending = request('swatch/checkout/', { product_id: config.productId, quantity: qty });
      } else {
        var d = dims();
        track('custom_size_add_attempt', { product_id: config.productId, shape: shape() });
        pending = request('custom-size/checkout/', {
          product_id: config.productId,
          shape: shape(),
          width_ft: parseInt(el.widthFt.value, 10),
          width_in: parseInt(el.widthIn.value, 10),
          length_ft: singleDimension() ? 0 : parseInt(el.lengthFt.value, 10),
          length_in: singleDimension() ? 0 : parseInt(el.lengthIn.value, 10),
          quantity: qty
        });
      }

      var properties = nonSizeSelections();
      pending.then(function (payload) {
        return addToCart(payload.variant_id, qty, properties).then(function () {
          track(kind + '_added', { variant_id: payload.variant_id });
          deselect();
        });
      }).catch(function (err) {
        track(kind + '_error', { message: err && err.message });
        showError((err && err.message) || config.errorText);
        if (el.panel && currentSelection() === 'custom') el.panel.hidden = false;
      }).then(function () {
        setBusy(btn, false);
      });
    }

    // Capture phase + stopImmediatePropagation: the theme binds its own submit
    // handler to this button, so letting the event through would ALSO add the
    // still-selected size variant alongside ours.
    scope.addEventListener('click', function (e) {
      if (!currentSelection()) return;
      var btn = e.target.closest && e.target.closest('button[name="add"], .add_to_cart_button');
      if (!btn || !scope.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      order(btn);
    }, true);

    /* Changing the SIZE select clears the special mode — a shopper who picks a
     * standard size there is choosing a standard variant, and keeping a mode
     * active past that point is how the sister store charged the wrong item.
     * A NON-size option (colour) is different in kind: it CONFIGURES the
     * custom rug or swatch rather than exiting it (the choice reaches the
     * order as a line-item property — see nonSizeSelections), so the mode
     * survives it and the observer re-asserts the mode's visuals over the
     * markup the theme re-renders. Capture phase because the theme stops
     * propagation on some controls. Typed panel dimensions survive — the
     * panel lives in our markup, outside the picker. */
    scope.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'SELECT' || root.contains(t)) return;
      // The "Custom size…" entry inside the theme's dropdown: revert the select
      // to its previous value (it is a door, not a size) and open the panel.
      // Capture phase runs before the theme's own @change handler, and
      // stopImmediatePropagation keeps the theme from trying to resolve the
      // sentinel to a variant.
      if (t.value === '__rso_custom__' || t.value === '__rso_swatch__') {
        var wantsSwatch = t.value === '__rso_swatch__';
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        var prev = t.getAttribute('data-rso-prev');
        if (prev !== null) t.value = prev;
        if (wantsSwatch && swatchUsable) select('swatch');
        if (!wantsSwatch && customSizeUsable) select('custom');
        return;
      }
      t.setAttribute('data-rso-prev', t.value);
      // Only a SIZE select exits the mode — the main picker's and the sticky
      // bar's alike. Our panel's selects (inside root) never reach here, and
      // non-size options keep the mode (rationale above).
      if (currentSelection() && isSizeSelect(t)) deselect();
    }, true);

    /* The dropdown's "Custom size…" entry ships DISABLED from Liquid so a
     * no-JS page shows it grayed rather than selectable-but-dead. Enable it
     * here (or remove it when the ladder cannot price this rug), and seed the
     * revert target. The theme replaces the picker's innerHTML on variant
     * change, recreating the option disabled — the observer below re-runs
     * this. Stateless by design: it only ever enables/removes options and
     * seeds data-rso-prev, so a missed tick degrades to a grayed entry, never
     * to a wrong purchase. */
    // Label sources captured ONCE at init, before any theme rebuild can
    // destroy the server-rendered options.
    var sentinelLabels = { custom: null, swatch: null };

    function isSizeSelect(sel) {
      var name = sel.getAttribute('name') || '';
      return /size/i.test(name);
    }

    function armSentinels() {
      Array.prototype.forEach.call(
        scope.querySelectorAll('option[data-rso-custom-option]'),
        function (opt) {
          if (root.contains(opt)) return;
          if (!sentinelLabels.custom) sentinelLabels.custom = opt.textContent.trim();
          if (!customSizeUsable) { opt.remove(); return; }
          opt.disabled = false;
        }
      );
      // SELF-HEAL: some theme update paths rebuild the size select from
      // variant data and drop the server-rendered entries entirely (observed
      // after variant changes — the section re-fetch carries them, the
      // rebuilt select does not). Recreate whatever is missing.
      Array.prototype.forEach.call(scope.querySelectorAll('select'), function (sel) {
        if (root.contains(sel) || !isSizeSelect(sel)) return;
        if (customSizeUsable && !sel.querySelector('option[data-rso-custom-option]')) {
          var oc = document.createElement('option');
          oc.value = '__rso_custom__';
          oc.setAttribute('data-rso-custom-option', '');
          oc.textContent = sentinelLabels.custom || 'Custom size…';
          sel.appendChild(oc);
        }
        if (swatchUsable && el.swatchBtn && !sel.querySelector('option[data-rso-swatch-option]')) {
          var os = document.createElement('option');
          os.value = '__rso_swatch__';
          os.setAttribute('data-rso-swatch-option', '');
          os.textContent = 'Order a swatch…';
          sel.appendChild(os);
        }
      });
      // The swatch entry's ELIGIBILITY lives here, not in Liquid (the material
      // rule renders the button, so the button's presence is the signal), and
      // its label is copied from the button so the priced text has exactly one
      // source ("Order a swatch — $29.99").
      Array.prototype.forEach.call(
        scope.querySelectorAll('option[data-rso-swatch-option]'),
        function (opt) {
          if (root.contains(opt)) return;
          if (!swatchUsable || !el.swatchBtn) { opt.remove(); return; }
          opt.disabled = false;
          // WRITE ONLY ON CHANGE. This function is re-run by a MutationObserver,
          // and setting textContent replaces the option's text node even when
          // the string is identical — which is itself a mutation, which
          // re-triggers the observer, forever. That exact loop shipped once and
          // made the dropdown flicker ~2x/second, blocking selection. The
          // equality guard is what lets the observer go quiet after one pass.
          var label = (el.swatchBtn.textContent || '').replace(/\s+/g, ' ').trim();
          if (label) sentinelLabels.swatch = label + '…';
          if (label && opt.textContent !== label + '…') opt.textContent = label + '…';
        }
      );
      Array.prototype.forEach.call(scope.querySelectorAll('select'), function (sel) {
        if (root.contains(sel)) return;
        if (!sel.hasAttribute('data-rso-prev') && sel.value !== '__rso_custom__') {
          sel.setAttribute('data-rso-prev', sel.value);
        }
      });
    }

    var armPending = null;
    new MutationObserver(function () {
      if (armPending) return;
      armPending = setTimeout(function () {
        armPending = null;
        armSentinels();
        // The theme re-renders the picker zone on variant changes, resurrecting
        // the static custom-quote link the widget replaces. Re-hide it.
        if (customSizeUsable) {
          Array.prototype.forEach.call(
            scope.querySelectorAll('[data-rso-fallback]'),
            function (node) { node.hidden = true; }
          );
        }
        // A theme re-render replaces price nodes; while idle, re-stash them so
        // a later mode activation has a trusted original to swap and restore.
        // While a mode is ACTIVE (a colour change re-rendered the section
        // under it), re-assert the mode's visuals over the fresh markup.
        if (!currentSelection()) syncPrice();
        else reassertMode();
      }, 50);
    }).observe(scope, { childList: true, subtree: true });

    bindModeButton(el.customBtn, 'custom');
    bindModeButton(el.swatchBtn, 'swatch');

    [el.shape, el.widthFt, el.widthIn, el.lengthFt, el.lengthIn].forEach(function (input) {
      if (input) input.addEventListener('change', function () {
        dimsTouched = true;
        renderPanel();
      });
    });

    // Exactly one of {this widget, the static custom-quote link} may appear.
    // Hiding it from here rather than gating it in Liquid is deliberate: the
    // final gate is "at least two PARSEABLE listed sizes", which needs the
    // variant-title parser above and cannot be expressed in Liquid. Failing
    // this way round is safe — if this script never runs, the link survives
    // and the shopper still has a route to a custom size.
    if (customSizeUsable) {
      Array.prototype.forEach.call(
        scope.querySelectorAll('[data-rso-fallback]'),
        function (node) { node.hidden = true; }
      );
    }

    armSentinels();
    presetDefaults();
    if (customSizeUsable) renderPanel();
  }

  function boot() {
    document.querySelectorAll('[data-rug-size-options]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor replaces whole sections (root, listeners and all); a
  // fresh boot re-initialises the replacement. data-rso-ready guards against
  // double-init on already-live roots.
  document.addEventListener('shopify:section:load', boot);
})();
